import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { CONTACT_EMAIL, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Under £25k", "£25k – £75k", "£75k – £150k", "£150k+", "Not sure yet"];
const TIMELINES = ["As soon as possible", "1–3 months", "3–6 months", "6+ months"];
const EMPTY = { name: "", email: "", phone: "", service: "", budget: "", timeline: "", location: "", message: "" };

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).filter(Boolean).join(" ") : "Something went wrong. Please try again.");

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#a7aebb]">{label}</span>
    <div className="mt-3">{children}</div>
  </label>
);

const inputCls = "w-full bg-transparent border-0 border-b border-[#3a4152] focus:border-[#d96b27] focus:outline-none py-3 text-lg placeholder:text-[#5b6375] transition-colors";

const Chips = ({ options, value, onChange, name }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button
        type="button"
        key={o}
        data-testid={`${name}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => onChange(value === o ? "" : o)}
        className={`px-4 py-2.5 text-sm border transition-colors ${value === o ? "bg-[#d96b27] border-[#d96b27] text-[#0c0e12]" : "border-[#262b38] text-[#c9ced8] hover:border-[#a7aebb]"}`}
      >
        {o}
      </button>
    ))}
  </div>
);

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Enquiry received — we'll be in touch shortly.");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact"
        lines={["Tell us about", "your project."]}
        intro="Share a few details and we will talk through how we would approach it — design, build or both."
      />

      <section className="mx-auto max-w-[1440px] px-5 sm:px-10 pb-28 sm:pb-36 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-4 space-y-10">
          <div>
            <Eyebrow>Email</Eyebrow>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="group mt-4 flex items-center gap-3 font-display text-xl sm:text-2xl font-bold break-all hover:text-[#d96b27] transition-colors">
              <Mail className="w-5 h-5 shrink-0 text-[#d96b27]" /> {CONTACT_EMAIL}
            </a>
          </div>
          <div>
            <Eyebrow>Studio</Eyebrow>
            <p className="mt-4 flex items-start gap-3 text-lg">
              <MapPin className="w-5 h-5 mt-1 shrink-0 text-[#d96b27]" /> Preston, Lancashire.<br />Working across the North West.
            </p>
          </div>
          <div className="border border-[#262b38] p-6 blueprint">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#a7aebb]">What happens next</p>
            <ol className="mt-4 space-y-3 text-[#c9ced8]">
              <li>01 — We read your brief</li>
              <li>02 — We call to talk it through</li>
              <li>03 — We arrange a site visit</li>
            </ol>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-8">
          {done ? (
            <motion.div data-testid="enquiry-success" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="border border-[#262b38] bg-[#14171f] p-10 sm:p-16 clip-frame">
              <CheckCircle2 className="w-12 h-12 text-[#d96b27]" strokeWidth={1.4} />
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-6">Thank you.</h2>
              <p className="text-[#a7aebb] text-lg mt-4 max-w-md">Your enquiry has been received. We will be in touch to talk through your project.</p>
              <button data-testid="enquiry-new-btn" onClick={() => setDone(false)} className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-[#d96b27] hover:text-[#f07d38]">
                Send another enquiry
              </button>
            </motion.div>
          ) : (
            <form data-testid="enquiry-form" onSubmit={submit} className="space-y-10">
              <div className="grid sm:grid-cols-2 gap-10">
                <Field label="Name *"><input data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" /></Field>
                <Field label="Email *"><input data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" /></Field>
                <Field label="Phone"><input data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Optional" /></Field>
                <Field label="Project location"><input data-testid="enquiry-location-input" value={form.location} onChange={set("location")} className={inputCls} placeholder="e.g. Preston, Chorley" /></Field>
              </div>
              <Field label="Service"><Chips name="service" options={SERVICES.map((s) => s.title)} value={form.service} onChange={set("service")} /></Field>
              <Field label="Budget"><Chips name="budget" options={BUDGETS} value={form.budget} onChange={set("budget")} /></Field>
              <Field label="Timeline"><Chips name="timeline" options={TIMELINES} value={form.timeline} onChange={set("timeline")} /></Field>
              <Field label="Your project *">
                <textarea data-testid="enquiry-message-input" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="What would you like to build or change?" />
              </Field>
              <button
                type="submit"
                disabled={loading}
                data-testid="enquiry-submit-btn"
                className="group inline-flex items-center gap-4 bg-[#d96b27] hover:bg-[#f07d38] disabled:opacity-60 text-[#0c0e12] pl-8 pr-6 py-5 font-mono text-sm uppercase tracking-[0.18em] transition-colors clip-frame-sm"
              >
                {loading ? "Sending" : "Send enquiry"}
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowUpRight className="w-5 h-5 transition-transform duration-500 group-hover:rotate-45" />}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}
